import React, { useState, useEffect, useRef, createContext } from "react";
import PropTypes from "prop-types";
import { Auth } from "aws-amplify";
import { LuLogOut, LuMoreVertical, LuUser, LuMenu, LuLifeBuoy, LuGauge } from "react-icons/lu";

import mainLogo from "../../../../assets/images/logo/logo.png";
import { getMyUserProfile } from "../../../../services/user/user-service";
import config from "../../../../config";
import { onPremSignOut } from "../../../../services/auth/on-prem-auth-service";
import AppSwitcher from "../header/app-switcher";
import ThemeSwitch from "../../../../components/theme-switch";
import { clearImpersonation, isImpersonating, isSupportStaff } from "../../../../services/auth/impersonation-service";

export const SidebarContext = createContext();

function MenuItem({ icon: Icon, label, onClick }) {
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className="group flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm font-medium text-ink transition-colors hover:bg-raised"
      >
        <Icon className="h-4 w-4 text-muted transition-colors group-hover:text-ink" aria-hidden="true" />
        <span>{label}</span>
      </button>
    </li>
  );
}

MenuItem.propTypes = { icon: PropTypes.elementType, label: PropTypes.string, onClick: PropTypes.func };

/** Small heading that separates groups of nav items. */
export function SidebarSectionLabel({ children }) {
  return (
    <div className="px-2.5 pb-1 pt-4 first:pt-1">
      <span className="text-[13px] font-medium text-muted">
        {children}
      </span>
    </div>
  );
}

SidebarSectionLabel.propTypes = { children: PropTypes.node };

const Sidebar = ({ children, open = true, onClose }) => {
  const expanded = true;
  const [isPopover, setIsPopover] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [supportStaff, setSupportStaff] = useState(false);
  const popoverRef = useRef(null);

  useEffect(() => {
    if (!config.IS_ON_PREM) isSupportStaff().then(setSupportStaff);
  }, []);

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const profile = await getMyUserProfile();
        setUserProfile(profile.response);
      } catch (error) {
        console.error("Failed to fetch user profile:", error);
      }
    };

    fetchUserProfile();
  }, []);

  // Dismiss the account popover when clicking anywhere outside it.
  useEffect(() => {
    if (!isPopover) return;
    const handler = (event) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target)) {
        setIsPopover(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isPopover]);

  const handleUserPopover = () => {
    setIsPopover((prev) => !prev);
  };

  const handleSignOut = async () => {
    clearImpersonation();
    if (config.IS_ON_PREM) {
      onPremSignOut();
    } else {
      await Auth.signOut();
    }
    window.location = "/";
  };

  return (
    <aside id="main-navigation" aria-hidden={!open} {...(!open ? { inert: '' } : {})} className={`fixed z-[12] h-screen w-56 transition-transform duration-200 motion-reduce:transition-none ${open ? 'translate-x-0' : '-translate-x-full'}`}>
      <nav className="flex h-full flex-col border-r border-line bg-rail">
        {/* Brand + app switcher */}
        <div className="flex h-14 shrink-0 items-center gap-2 px-3">
          <div className="flex h-8 w-8 flex-none items-center justify-center">
            <img
              src={mainLogo}
              alt="BotCircuits"
              className="h-7 w-7 flex-shrink-0 object-contain dark:invert"
            />
          </div>
          <div className="min-w-0 flex-1 [&>div>button]:max-w-full"><AppSwitcher /></div>
          <button type="button" aria-label="Close navigation" aria-expanded={open} aria-controls="main-navigation" onClick={onClose} className="shrink-0 rounded-lg p-1 text-muted transition-colors hover:bg-raised hover:text-ink"><LuMenu size={16} aria-hidden="true" /></button>
        </div>

        <div className="shrink-0 border-t border-line" />

        <SidebarContext.Provider value={{ expanded }}>
          <ul className="flex-1 overflow-hidden px-2 pt-1">{children}</ul>
        </SidebarContext.Provider>

        {/* Account footer */}
        <div className="border-t border-line p-2.5">
          <div
            ref={popoverRef}
            className="relative flex items-center gap-2.5 rounded-lg p-1.5 transition-colors hover:bg-raised/60"
          >
            <div className="flex h-8 w-8 flex-none items-center justify-center rounded-lg bg-accent text-accent-ink">
              <span className="text-sm font-semibold">
                {userProfile ? userProfile.name.charAt(0).toUpperCase() : "A"}
              </span>
            </div>

            <div className="min-w-0 flex-1 leading-tight">
              <p className="truncate text-[13px] font-semibold text-ink">
                {userProfile ? userProfile.name : "..."}
              </p>
              <p className="truncate text-[11px] text-muted">
                {userProfile ? userProfile.email : "..."}
              </p>
            </div>

            <button
              onClick={handleUserPopover}
              aria-label="Account menu"
              aria-expanded={isPopover}
              className="flex h-7 w-7 flex-none items-center justify-center rounded-md text-muted transition-colors hover:bg-raised hover:text-ink"
            >
              <LuMoreVertical className="h-4 w-4" />
            </button>

            {expanded && isPopover && (
              <div
                className="absolute bottom-11 left-0 right-0 z-50 overflow-hidden rounded-2xl border border-line bg-panel
                           shadow-lg ring-1 ring-black/5"
              >
                <div className="px-3 pb-3 pt-2.5">
                  <p className="truncate px-1 text-[13px] text-muted">{userProfile ? userProfile.email : "..."}</p>
                  <ThemeSwitch className="mt-2" />
                </div>
                <ul className="border-t border-line py-1">
                  <MenuItem icon={LuUser} label="Profile settings" onClick={() => (window.location = "/user-settings/profile")} />
                  {supportStaff && !isImpersonating() && (
                    <MenuItem icon={LuLifeBuoy} label="Support" onClick={() => (window.location = "/support")} />
                  )}
                  {supportStaff && !isImpersonating() && (
                    <MenuItem icon={LuGauge} label="Subscription update" onClick={() => (window.location = "/support/subscription-update")} />
                  )}
                </ul>
                <ul className="border-t border-line py-1">
                  <MenuItem icon={LuLogOut} label="Log out" onClick={handleSignOut} />
                </ul>
              </div>
            )}
          </div>
        </div>
      </nav>
    </aside>
  );
};

export default Sidebar;

Sidebar.propTypes = {
  children: PropTypes.node,
  open: PropTypes.bool,
  onClose: PropTypes.func,
};
